/**
 * Swarm List Component
 * Displays list of swarms with status
 */

import React, { useEffect, useState } from 'react';
import { SwarmClient } from '@mindcore/sdk';
import { 
  Plus, 
  ExternalLink, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Loader2,
  ChevronRight,
} from 'lucide-react';

interface SwarmListProps {
  client: SwarmClient;
  recentSwarms: string[];
  onViewSwarm: (swarmId: string) => void;
  onCreateNew: () => void;
}

interface SwarmSummary {
  id: string;
  prompt: string;
  status: 'active' | 'completed' | 'failed' | 'paused';
  phase: string;
  createdAt: string;
  progress: number;
}

const PHASE_LABELS: Record<string, string> = {
  idle: 'Idle',
  blueprinting: 'Designing',
  swarm_initializing: 'Initializing',
  parallel_implementation: 'Building',
  multi_review: 'Reviewing',
  consensus_reached: 'Consensus',
  security_validation: 'Security Audit',
  deployment_prep: 'Preparing Deploy',
  multi_cloud_deploy: 'Deploying',
  completed: 'Completed',
  failed: 'Failed',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  paused: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

export const SwarmList: React.FC<SwarmListProps> = ({
  client,
  recentSwarms,
  onViewSwarm,
  onCreateNew,
}) => {
  const [swarms, setSwarms] = useState<SwarmSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSwarms();
  }, [recentSwarms]);

  const loadSwarms = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load swarms from recent list
      const loadedSwarms: SwarmSummary[] = [];
      
      for (const swarmId of recentSwarms) {
        try {
          const session = await client.connect(swarmId);
          const status = await session.getStatus();
          
          loadedSwarms.push({
            id: swarmId,
            prompt: status.prompt.slice(0, 100) + (status.prompt.length > 100 ? '...' : ''),
            status: status.status,
            phase: status.phase,
            createdAt: new Date(status.createdAt).toLocaleString(),
            progress: calculateProgress(status.phase),
          });
        } catch {
          // Skip failed loads
        }
      }

      setSwarms(loadedSwarms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load swarms');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Your Swarms</h2>
          <p className="text-gray-600">
            {swarms.length > 0 
              ? `${swarms.length} swarm${swarms.length > 1 ? 's' : ''} found`
              : 'No swarms yet. Create your first one!'
            }
          </p>
        </div>
        <button
          onClick={onCreateNew}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          <span>Create Swarm</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Swarm Grid */}
      {swarms.length > 0 ? (
        <div className="grid gap-4">
          {swarms.map((swarm) => (
            <div
              key={swarm.id}
              onClick={() => onViewSwarm(swarm.id)}
              className="bg-white rounded-xl shadow-sm border p-6 cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${STATUS_COLORS[swarm.status]}`}>
                      {swarm.status.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-500">
                      {PHASE_LABELS[swarm.phase] || swarm.phase}
                    </span>
                    {swarm.status === 'active' && (
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    )}
                  </div>
                  <p className="text-gray-900 font-medium mb-2">
                    {swarm.prompt}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {swarm.createdAt}
                    </span>
                    <span className="font-mono text-xs">
                      ID: {swarm.id.slice(0, 8)}...
                    </span>
                  </div>
                </div>

                {/* Progress Ring */}
                <div className="flex items-center space-x-4">
                  <div className="relative w-16 h-16">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="4"
                      />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={`${swarm.progress * 1.76} 176`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-700">
                        {swarm.progress}%
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${swarm.progress}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No swarms yet
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create your first agent swarm to start building production-ready applications 
            with our 7-agent AI swarm.
          </p>
          <button
            onClick={onCreateNew}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            <span>Create Your First Swarm</span>
          </button>
        </div>
      )}
    </div>
  );
};

function calculateProgress(phase: string): number {
  const phaseProgress: Record<string, number> = {
    idle: 0,
    blueprinting: 10,
    swarm_initializing: 20,
    parallel_implementation: 40,
    multi_review: 60,
    consensus_reached: 70,
    security_validation: 80,
    deployment_prep: 90,
    multi_cloud_deploy: 95,
    completed: 100,
    failed: 0,
  };

  return phaseProgress[phase] || 0;
}
